import { Suspense } from 'react';
import { NavLink, Outlet } from 'react-router';
import { Avatar, Button, EmptyState, IconShield, SkeletonRows, ToastHost } from '../components';
import { signOut } from '../lib/auth';
import { useSession } from '../lib/session';
import { MASTER_NAV } from './nav';
import styles from './shell.module.css';

/**
 * The master console shell.
 *
 * It carries `otto-dark` from tokens.css, which repoints the surface tokens to
 * the deep-teal set. That is deliberate and load-bearing: an operator who has
 * just used operator mode to enter a client console must never be uncertain
 * about which console they are typing into. Same components, opposite ground.
 */
export function MasterShell() {
  const session = useSession();

  if (!session.isLoading && !session.isOttoStaff) {
    return (
      <div className={`otto-dark ${styles.shell}`}>
        <div className={styles.main}>
          <div className={styles.masterStripe} aria-hidden="true" />
          <main className={styles.content} id="main-content" tabIndex={-1}>
            <EmptyState
              icon={<IconShield />}
              title="The master console is Otto staff only"
              body="Your account does not carry the Otto operator role. If you believe that is wrong, ask your Otto contact — the role is granted in Entra, not here."
              actions={
                // Not a link back to the client console: that is a different
                // app on a different origin now, so there is nothing here to
                // navigate to. Signing out is the only useful action.
                <Button variant="primary" onClick={signOut}>
                  Sign out
                </Button>
              }
            />
          </main>
        </div>
        <ToastHost />
      </div>
    );
  }

  return (
    <div className={`otto-dark ${styles.shell} ${styles.masterShell}`}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <aside className={styles.sidebar} aria-label="Master console sections">
        <NavLink to="/master" className={styles.brand} end>
          <span className={styles.brandMark} aria-hidden="true">
            iQ
          </span>
          <span className={styles.brandText}>
            <span className={styles.brandName}>Otto IQ</span>
            <span className={styles.brandTenant}>Master console</span>
          </span>
        </NavLink>

        <span className={styles.masterBadge}>
          <IconShield size={14} />
          Otto staff
        </span>

        <nav className={styles.nav} aria-label="Master console">
          <div className={styles.navGroup}>
            {MASTER_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    [styles.navLink, isActive ? styles.navLinkActive : null]
                      .filter(Boolean)
                      .join(' ')
                  }
                >
                  <Icon className={styles.navIcon} size={18} />
                  <span className={styles.navLabel}>{item.label}</span>
                  {item.pending ? (
                    <span className={styles.navPending} title="Not built yet">
                      soon
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </div>
        </nav>

        <div className={styles.footer}>
          <div className={styles.footerPerson}>
            <Avatar name={session.person?.name ?? 'Otto staff'} kind="person" size="md" />
            <span className={styles.footerText}>
              {/*
                The fallbacks say "we do not know yet", not a name. "Otto staff"
                and "operator" read like a signed-in identity, which is
                misleading while `/me` is still in flight.
              */}
              <span className={styles.footerName}>
                {session.person?.name ?? (session.isLoading ? 'Signing in…' : 'Otto staff')}
              </span>
              <span className={styles.footerMeta}>
                {session.isLoading ? '—' : (session.person?.email ?? 'no linked account')}
              </span>
            </span>
          </div>
          {/*
            No "leave master console" link. This build and the client console are
            separate apps on separate origins with separate Entra registrations;
            there is nowhere here to navigate to. Operator mode is how staff
            enter a client tenant, and it exits back to /master on its own.
          */}
          <div className={styles.footerLinks}>
            <Button variant="ghost" size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <div className={styles.main}>
        <div className={styles.masterStripe} aria-hidden="true" />
        <main className={styles.content} id="main-content" tabIndex={-1}>
          <Suspense fallback={<SkeletonRows rows={5} />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <ToastHost />
    </div>
  );
}

export default MasterShell;
